import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ProductData } from '../types';
import { MaterialsService, ColorsService } from '../../../../api';
import type { MergedProductUnit } from '../productUnitsMerge.utils';

export type ProductUnit = MergedProductUnit;

export interface ProductGroup {
  productName: string;
  productCode: string;
  productId: number;
  units: ProductUnit[];
  totalQuantity: number;
}

/** Max unit rows rendered per product accordion (keeps the screen responsive at 100+ qty). */
export const PRODUCT_UNITS_PAGE_SIZE = 50;

export interface ProductUnitPageSlice {
  units: ProductUnit[];
  page: number;
  totalPages: number;
  totalUnits: number;
  pageStart: number;
  pageEnd: number;
}

export function getProductUnitPageCount(totalUnits: number): number {
  if (totalUnits <= 0) return 1;
  return Math.ceil(totalUnits / PRODUCT_UNITS_PAGE_SIZE);
}

export function sliceProductUnitsPage(units: ProductUnit[], page: number): ProductUnitPageSlice {
  const totalUnits = units.length;
  const totalPages = getProductUnitPageCount(totalUnits);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * PRODUCT_UNITS_PAGE_SIZE;
  const endIndex = Math.min(startIndex + PRODUCT_UNITS_PAGE_SIZE, totalUnits);

  return {
    units: units.slice(startIndex, endIndex),
    page: safePage,
    totalPages,
    totalUnits,
    pageStart: totalUnits === 0 ? 0 : startIndex + 1,
    pageEnd: endIndex,
  };
}

/** Item local para alimentar el widget de búsqueda (`ProductForm`) de la barra "Agregar producto". */
export interface DraftItem {
  id: number;
  productName: string;
  productCode: string;
  quantity: string;
  productId?: number;
  workshopCode?: string;
}

const EMPTY_DRAFT: DraftItem = { id: -1, productName: '', productCode: '', quantity: '1' };

export interface InventoryItemsStepProps {
  productUnits: ProductUnit[];
  products: ProductData[];
  selectedSupplierId?: string;
  onAddProduct: (product: ProductData, quantity: number) => void | Promise<void>;
  onRemoveUnit: (unitId: string) => void;
  onRemoveSection: (productId: number) => void;
  onSetSectionQuantity: (productId: number, quantity: number) => void | Promise<void>;
  /** ±N against live section length (header buttons). */
  onAdjustSectionQuantity: (productId: number, delta: number) => void | Promise<void>;
  onUpdateUnitField: (
    unitId: string,
    section: 'properties' | 'costs',
    patch: Record<string, string>,
  ) => void;
  onResetSection: (productId: number) => void | Promise<void>;
}

export const useInventoryItemsStep = ({
  productUnits,
  products,
  onAddProduct,
  onRemoveUnit,
  onRemoveSection,
  onSetSectionQuantity,
  onAdjustSectionQuantity,
  onUpdateUnitField,
  onResetSection,
}: InventoryItemsStepProps) => {
  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([]);
  const [colors, setColors] = useState<{ id: number; name: string }[]>([]);

  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [unitPageByProduct, setUnitPageByProduct] = useState<Record<number, number>>({});
  const [editableColumns, setEditableColumns] = useState<
    Record<
      number,
      {
        materialId: boolean;
        weight: boolean;
        length: boolean;
        diameter: boolean;
        color: boolean;
        workshopCode: boolean;
        costPrice: boolean;
        salePrice: boolean;
      }
    >
  >({});

  // Producto pendiente de confirmación para borrado de sección completa.
  const [sectionPendingDelete, setSectionPendingDelete] = useState<number | null>(null);

  // Estado local de la barra "Agregar producto" (no se commitea hasta hacer click en Agregar).
  const [draftItem, setDraftItem] = useState<DraftItem>(EMPTY_DRAFT);

  const prevProductIdsRef = useRef<number[] | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await MaterialsService.materialsControllerFindAll(1, 1000);
        const data = res && Array.isArray((res as any).data) ? (res as any).data : res;
        setMaterials(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching materials:', error);
        setMaterials([]);
      }
    })();
    (async () => {
      try {
        const res = await ColorsService.colorsControllerFindAll(1, 1000);
        const data = res && Array.isArray((res as any).data) ? (res as any).data : res;
        setColors(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching colors:', error);
        setColors([]);
      }
    })();
  }, []);

  // Agrupar piezas por productId (estable), preservando el orden de aparición.
  const productGroups = useMemo<ProductGroup[]>(() => {
    const order: number[] = [];
    const byId = new Map<number, ProductUnit[]>();
    for (const unit of productUnits) {
      if (!byId.has(unit.productId)) {
        byId.set(unit.productId, []);
        order.push(unit.productId);
      }
      byId.get(unit.productId)!.push(unit);
    }
    return order.map((productId) => {
      const units = byId.get(productId)!;
      const first = units[0];
      return {
        productName: first.productName,
        productCode: first.productCode,
        productId: first.productId,
        units,
        totalQuantity: units.length,
      };
    });
  }, [productUnits]);

  // Al agregar un producto nuevo, su sección se abre y las demás se cierran (solo una a la vez:
  // rendimiento con muchas secciones de 50+ piezas). Al quitar la sección abierta, no dejar
  // ninguna expandida.
  useEffect(() => {
    const currentIds = productGroups.map((g) => g.productId);
    const prevIds = prevProductIdsRef.current;

    if (prevIds === null) {
      // Primera carga (p. ej. editando un borrador existente): abrir la primera sección.
      setExpandedProductId(currentIds[0] ?? null);
    } else {
      const addedId = currentIds.find((id) => !prevIds.includes(id));
      if (addedId != null) {
        setExpandedProductId(addedId);
      } else if (expandedProductId != null && !currentIds.includes(expandedProductId)) {
        setExpandedProductId(null);
      }
    }
    prevProductIdsRef.current = currentIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGroups]);

  const toggleProductExpansion = useCallback((productId: number) => {
    setExpandedProductId((prev) => (prev === productId ? null : productId));
    setUnitPageByProduct((prev) => ({ ...prev, [productId]: prev[productId] ?? 1 }));
  }, []);

  const getUnitPageSlice = useCallback(
    (productId: number, groupUnits: ProductUnit[]): ProductUnitPageSlice => {
      const page = unitPageByProduct[productId] ?? 1;
      return sliceProductUnitsPage(groupUnits, page);
    },
    [unitPageByProduct],
  );

  const setUnitPage = useCallback((productId: number, page: number) => {
    setUnitPageByProduct((prev) => ({ ...prev, [productId]: page }));
  }, []);

  const toggleColumnEditable = useCallback(
    (
      productId: number,
      column:
        | 'materialId'
        | 'weight'
        | 'length'
        | 'diameter'
        | 'color'
        | 'workshopCode'
        | 'costPrice'
        | 'salePrice',
    ) => {
      setEditableColumns((prev) => ({
        ...prev,
        [productId]: {
          ...(prev[productId] ?? {
            materialId: false,
            weight: false,
            length: false,
            diameter: false,
            color: false,
            workshopCode: false,
            costPrice: false,
            salePrice: false,
          }),
          [column]: !(prev[productId]?.[column] ?? false),
        },
      }));
    },
    [],
  );

  const calculateMargin = useCallback((costPrice: string, salePrice: string) => {
    const cost = parseFloat(costPrice) || 0;
    const sale = parseFloat(salePrice) || 0;
    if (sale <= 0) return '0.00';
    return (((sale - cost) / sale) * 100).toFixed(2);
  }, []);

  const handleUnitChange = useCallback(
    (unitId: string, section: 'properties' | 'costs', field: string, value: string) => {
      if (section === 'properties' && field === 'materialId') {
        const selectedMaterial = materials.find((m) => m.id.toString() === value);
        onUpdateUnitField(unitId, section, {
          materialId: value,
          materialName: selectedMaterial?.name ?? '',
        });
        return;
      }
      onUpdateUnitField(unitId, section, { [field]: value });
    },
    [materials, onUpdateUnitField],
  );

  // --- Barra "Agregar producto" ---

  const currentProductIds = useMemo(() => productGroups.map((g) => g.productId), [productGroups]);

  // `ProductForm` (reusado para esta barra) siempre reporta productId como STRING
  // (`onUpdate(id, 'productId', product.id.toString())`), aunque DraftItem lo declare number.
  // Normalizar acá, en el único punto de entrada, evita que el resto del código compare
  // number contra string (comparación que nunca es verdadera) y crea piezas en blanco aunque
  // el producto sí esté en caché.
  const handleDraftUpdate = useCallback((_id: number, field: keyof DraftItem, value: string) => {
    setDraftItem((prev) => {
      if (field === 'productId') {
        const parsed = value === '' ? undefined : Number(value);
        return { ...prev, productId: Number.isFinite(parsed) ? parsed : undefined };
      }
      return { ...prev, [field]: value } as DraftItem;
    });
  }, []);

  const isDraftValid = useMemo(() => {
    const qty = parseInt(draftItem.quantity, 10);
    return draftItem.productId != null && Number.isFinite(qty) && qty > 0;
  }, [draftItem]);

  const [isAddingProduct, setIsAddingProduct] = useState(false);

  const handleAddFromDraft = useCallback(async () => {
    if (!isDraftValid || draftItem.productId == null || isAddingProduct) return;
    const quantity = Math.max(1, parseInt(draftItem.quantity, 10) || 1);
    const product = products.find((p) => p.id === draftItem.productId);
    const fallbackProduct: ProductData = {
      id: draftItem.productId,
      name: draftItem.productName,
      code: draftItem.productCode,
      workshopCode: draftItem.workshopCode,
    };
    setIsAddingProduct(true);
    try {
      // onAddProduct puede tardar un instante si el producto no está en caché local todavía
      // (espera su data de catálogo antes de crear las piezas).
      await onAddProduct(product ?? fallbackProduct, quantity);
      setDraftItem(EMPTY_DRAFT);
    } finally {
      setIsAddingProduct(false);
    }
  }, [isDraftValid, isAddingProduct, draftItem, products, onAddProduct]);

  // --- Basurero de sección (con confirmación) ---

  const requestRemoveSection = useCallback((productId: number) => {
    setSectionPendingDelete(productId);
  }, []);

  const confirmRemoveSection = useCallback(() => {
    if (sectionPendingDelete != null) {
      onRemoveSection(sectionPendingDelete);
    }
    setSectionPendingDelete(null);
  }, [sectionPendingDelete, onRemoveSection]);

  const cancelRemoveSection = useCallback(() => {
    setSectionPendingDelete(null);
  }, []);

  // --- Cantidad de cabecera (agrega/quita piezas al final; sin confirmación) ---

  const handleHeaderQuantityChange = useCallback(
    (productId: number, rawValue: string) => {
      const next = parseInt(rawValue, 10);
      if (!Number.isFinite(next) || next < 1) return;
      onSetSectionQuantity(productId, next);
    },
    [onSetSectionQuantity],
  );

  /** Botones － / ＋: delta applied on live section length (race-safe). */
  const handleHeaderQuantityStep = useCallback(
    (productId: number, delta: 1 | -1) => {
      void onAdjustSectionQuantity(productId, delta);
    },
    [onAdjustSectionQuantity],
  );

  return {
    // Catálogos
    materials,
    colors,

    // Secciones
    productGroups,
    expandedProductId,
    toggleProductExpansion,
    editableColumns,
    toggleColumnEditable,
    productUnitsPageSize: PRODUCT_UNITS_PAGE_SIZE,
    getUnitPageSlice,
    setUnitPage,
    calculateMargin,
    handleUnitChange,
    onRemoveUnit,
    onResetSection,
    handleHeaderQuantityChange,
    handleHeaderQuantityStep,

    // Barra agregar producto
    draftItem,
    currentProductIds,
    handleDraftUpdate,
    isDraftValid,
    isAddingProduct,
    handleAddFromDraft,

    // Borrado de sección
    sectionPendingDelete,
    requestRemoveSection,
    confirmRemoveSection,
    cancelRemoveSection,
  };
};
